/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx
 *
 * Only the data, columns and labels are ours. Reuses the same source brand's own columns.tsx
 * cites (specs/017-isomorphic-theme-only/contracts/screen-map.md row 10) — there is no separate
 * tree/hierarchy template component to point at instead (`components/shape/tree.tsx` is a
 * decorative SVG, not a data component).
 */
import type { ColumnDef } from "@tanstack/react-table";
import { cn } from "@steelyard/theme";
import { imageUrl } from "@steelyard/common";
import type { Category } from "../../../lib/services/catalog";
import { ActionIcon, Avatar, Badge, Text } from "rizzui";
import {
  PiCaretRightBold as CaretRight,
  PiPackageDuotone as ProductsIcon,
  PiEyeDuotone as EyeIcon, PiPencilSimpleDuotone as PencilIcon,
} from "react-icons/pi";
import { RowActions } from "../../../components/table/row-actions";
import { StatusToggle } from "../../../components/table/status-toggle";
import { categoryPath } from "../../../lib/paths";

/**
 * `enableSorting: false` on every column, explicitly. The template's `useTanStackTable` wires
 * `getSortedRowModel()` and TanStack defaults every column to sortable unless told otherwise
 * (MainTable renders a clickable header toggle whenever `column.getCanSort()` is true). Brand's
 * columns never needed this — brand order carries no meaning — but category order
 * (parents-before-children, from `GET /categories/tree`) IS FR-008's "hierarchy without
 * re-sorting", and it must not be one accidental header click away from being destroyed.
 */
export function categoryColumns(opts: {
  /** Whether this category has any row beneath it in the tree — gets a twisty instead of an elbow icon. */
  hasChildren: (id: string) => boolean;
  /** Collapsed state lives in `List.tsx`, which also does the filtering — this column only reads it. */
  isCollapsed: (id: string) => boolean;
  onToggle: (id: string) => void;
  /** Present only with write permission — the status cell is then a switch. */
  setStatus?: (category: Category, active: boolean) => Promise<unknown>;
}): ColumnDef<Category>[] {
  const { hasChildren, isCollapsed, onToggle, setStatus } = opts;
  return [
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 320,
      enableSorting: false,
      // Indentation IS the hierarchy rendering FR-008 asks for — depth comes straight from the
      // API's `CategoryView.depth`, never recomputed by walking parentId here. Only a row with
      // rows beneath it gets a twisty — a childless leaf gets none, so the arrow never implies a
      // control that does nothing when clicked. `List.tsx`'s `rowClassName` shades every non-root
      // row so a parent's visible children still read as one indented group under it.
      cell: ({ row }) => {
        const { id, depth, name, imageHash } = row.original;
        const expandable = hasChildren(id);
        const collapsed = isCollapsed(id);
        return (
          <div className="flex items-center gap-1" style={{ paddingInlineStart: depth * 28 }}>
            <span className="flex size-7 shrink-0 items-center justify-center">
              {expandable ? (
                <ActionIcon
                  type="button"
                  size="sm"
                  variant="text"
                  className="text-gray-500 hover:text-gray-900"
                  aria-label={collapsed ? `Show sub-categories of ${name}` : `Hide sub-categories of ${name}`}
                  aria-expanded={!collapsed}
                  onClick={() => onToggle(id)}
                >
                  <CaretRight aria-hidden className={cn("size-3.5 transition-transform", !collapsed && "rotate-90")} />
                </ActionIcon>
              ) : null}
            </span>
            <Avatar name={name} src={imageHash ? imageUrl(imageHash) : ""} size="sm" />
            <Text className={cn("truncate", depth === 0 ? "font-semibold" : "text-gray-500")}>{name}</Text>
          </div>
        );
      },
    },
    {
      id: "code",
      accessorKey: "code",
      header: "Code",
      size: 150,
      enableSorting: false,
      cell: ({ row }) => (
        <Badge variant="flat" color="secondary" rounded="md" className="font-mono">
          {row.original.code}
        </Badge>
      ),
    },
    {
      id: "sortOrder",
      header: "Sort order",
      size: 110,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => <Text className="tabular-nums text-muted-foreground">{row.original.sortOrder}</Text>,
    },
    {
      id: "status",
      header: "Status",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <StatusToggle name={row.original.name} active={row.original.isActive} onChange={setStatus && ((next) => setStatus(row.original, next))} />
      ),
    },
    // No delete: deactivation is T047's scope, on the detail page behind a ReasonDialog. Edit/move
    // both live on the detail page's own forms (a pencil; "View" for a read-only role) — same as brand.
    {
      id: "actions",
      header: "",
      size: 110,
      enableSorting: false,
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          primary={{ label: setStatus ? "Edit category" : "View category", icon: setStatus ? PencilIcon : EyeIcon, to: categoryPath(row.original.slug) }}
          more={[{ label: "Products in this category", icon: ProductsIcon, to: `/catalogue/products?categoryId=${row.original.id}` }]}
        />
      ),
    },
  ];
}
